import { useState, useEffect } from 'react';
import { BookOpen, Network, Lock, Globe, Copy, Check } from 'lucide-react';
import { CodeBlock } from '../components/CodeBlock';

export function Help() {
  const [activeTab, setActiveTab] = useState<'dns' | 'ssl' | 'site'>('dns');
  const [appUrl, setAppUrl] = useState('website-paas-vefsj.ondigitalocean.app');
  const [copiedUrl, setCopiedUrl] = useState(false);

  useEffect(() => {
    // Try to determine the app URL from window location or fetch from API
    const currentHost = window.location.host;
    if (currentHost.includes('ondigitalocean.app')) {
      setAppUrl(currentHost);
    }
  }, []);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(appUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const tabs = [
    { id: 'dns' as const, label: 'DNS Setup', icon: Network },
    { id: 'ssl' as const, label: 'SSL Certificates', icon: Lock },
    { id: 'site' as const, label: 'Add First Site', icon: Globe },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="w-8 h-8 text-primary-500" />
          <h1 className="text-3xl font-bold text-white">Getting Started Guide</h1>
        </div>
        <p className="text-gray-400">
          Complete setup instructions for your SiteHub platform
        </p>
      </div>

      {/* Server Info Card */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h2 className="text-xl font-semibold text-white mb-4">Your Platform URL</h2>
        <div className="flex items-center justify-between bg-gray-900 rounded-lg p-4">
          <div>
            <span className="text-gray-400 text-sm">DigitalOcean App URL:</span>
            <p className="text-white font-mono text-lg">{appUrl}</p>
          </div>
          <button
            onClick={handleCopyUrl}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            {copiedUrl ? (
              <>
                <Check className="w-4 h-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy URL
              </>
            )}
          </button>
        </div>
        <p className="text-gray-500 text-sm mt-2">
          Use this URL as the target for all CNAME DNS records
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {/* Tab Headers */}
        <div className="flex border-b border-gray-700">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-gray-900 text-primary-500 border-b-2 border-primary-500'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'dns' && <DNSContent appUrl={appUrl} />}
          {activeTab === 'ssl' && <SSLContent />}
          {activeTab === 'site' && <SiteContent />}
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h2 className="text-xl font-semibold text-white mb-4">Additional Resources</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a
            href="/sites"
            className="flex items-center gap-3 p-4 bg-gray-900 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Globe className="w-5 h-5 text-primary-500" />
            <div>
              <div className="text-white font-medium">Manage Sites</div>
              <div className="text-gray-400 text-sm">Create and configure your sites</div>
            </div>
          </a>
          <a
            href="/dns"
            className="flex items-center gap-3 p-4 bg-gray-900 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <Network className="w-5 h-5 text-primary-500" />
            <div>
              <div className="text-white font-medium">DNS Management</div>
              <div className="text-gray-400 text-sm">Configure DNS records</div>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}

function DNSContent({ appUrl }: { appUrl: string }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-semibold text-white mb-3">DNS Configuration</h3>
        <p className="text-gray-400">
          Point your domains to the platform by configuring CNAME records at your domain registrar or DNS provider.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 1: Access Your DNS Provider</h4>
          <p className="text-gray-300 mb-2">
            Log in to your DNS provider's control panel. Common providers include:
          </p>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4">
            <li>DigitalOcean DNS (recommended - automated management available)</li>
            <li>Cloudflare</li>
            <li>GoDaddy</li>
            <li>Namecheap</li>
            <li>Google Domains / Google Cloud DNS</li>
          </ul>
        </div>

        <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
          <h4 className="text-green-400 font-medium mb-2">✨ Platform Target URL</h4>
          <p className="text-gray-300 mb-2">All your CNAME records should point to:</p>
          <code className="block text-white bg-gray-900 px-3 py-2 rounded font-mono">{appUrl}</code>
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 2: Add CNAME Record for Your Domain</h4>
          <p className="text-gray-300 mb-3">
            For example, if you're setting up <strong>upraxos.com</strong>:
          </p>
          <CodeBlock
            code={`Type: CNAME
Name: @ (or leave blank for root domain)
Value: ${appUrl}
TTL: 3600 (or Auto)

Note: Some providers don't allow CNAME at root (@).
Use ALIAS or ANAME if available, or use a subdomain instead.`}
            title="Root Domain CNAME Record"
          />
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 3: Add WWW Subdomain (Recommended)</h4>
          <p className="text-gray-300 mb-3">
            To support www.upraxos.com, add another CNAME:
          </p>
          <CodeBlock
            code={`Type: CNAME
Name: www
Value: ${appUrl}
TTL: 3600`}
            title="WWW Subdomain CNAME"
          />
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 4: Configure Preview Subdomain (Optional)</h4>
          <p className="text-gray-300 mb-3">
            To enable preview URLs for all sites (e.g., <code className="bg-gray-900 px-2 py-1 rounded text-sm">mysite.preview.upraxos.com</code>):
          </p>
          <CodeBlock
            code={`Type: CNAME
Name: *.preview
Value: ${appUrl}
TTL: 3600

This wildcard allows all sites to be previewed at:
{site-slug}.preview.upraxos.com`}
            title="Preview Wildcard CNAME"
          />
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 5: Verify DNS Propagation</h4>
          <p className="text-gray-300 mb-3">
            Wait 5-10 minutes for DNS to propagate, then verify:
          </p>
          <CodeBlock
            code={`# Check CNAME record
dig upraxos.com CNAME +short

# Or check full resolution
dig upraxos.com +short

# Expected output should include:
# ${appUrl}`}
            title="Verify DNS (Terminal)"
            language="bash"
          />
        </div>

        <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-4">
          <h4 className="text-purple-400 font-medium mb-2">📝 Example: Complete DNS Setup</h4>
          <p className="text-gray-300 mb-3">
            If you're setting up <strong>upraxos.com</strong> and want both the main site and admin dashboard:
          </p>
          <CodeBlock
            code={`# Main website (upraxos.com)
Type: CNAME, Name: @, Value: ${appUrl}

# WWW subdomain (www.upraxos.com)
Type: CNAME, Name: www, Value: ${appUrl}

# Admin dashboard (admin.upraxos.com)
Type: CNAME, Name: admin, Value: ${appUrl}

# Preview feature (*.preview.upraxos.com)
Type: CNAME, Name: *.preview, Value: ${appUrl}

# Additional site (blog.upraxos.com)
Type: CNAME, Name: blog, Value: ${appUrl}`}
            title="Complete DNS Configuration Example"
          />
        </div>

        <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
          <h4 className="text-blue-400 font-medium mb-2">💡 Provider-Specific Tips</h4>
          <div className="text-gray-300 text-sm space-y-2">
            <p><strong>DigitalOcean:</strong> Use the DNS management page in this dashboard for automated setup!</p>
            <p><strong>Cloudflare:</strong> CNAME flattening allows root domain CNAME. Orange cloud (proxy) is optional but works fine.</p>
            <p><strong>GoDaddy:</strong> Use @ for root (or CNAME Flattening). Some plans require ALIAS for root domain.</p>
            <p><strong>Namecheap:</strong> Use @ for root domain with CNAME. May require "CNAME Flattening" option enabled.</p>
            <p><strong>No CNAME at root?</strong> Check if your provider supports ALIAS or ANAME records, or use a subdomain (www.upraxos.com).</p>
          </div>
        </div>

        <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
          <h4 className="text-yellow-400 font-medium mb-2">⚠️ Important Notes</h4>
          <ul className="text-gray-300 text-sm space-y-1 list-disc list-inside">
            <li><strong>Single Target:</strong> ALL domains point to the same URL (<code className="bg-gray-900 px-1 rounded">{appUrl}</code>)</li>
            <li><strong>VHost Routing:</strong> The platform automatically routes requests based on the domain name</li>
            <li><strong>SSL Certificates:</strong> Auto-provisioned by DigitalOcean for all configured domains</li>
            <li><strong>Propagation Time:</strong> Usually 5-10 minutes, but can take up to 48 hours globally</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function SSLContent() {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-semibold text-white mb-3">SSL/TLS Certificate Setup</h3>
        <p className="text-gray-400">
          Secure your sites with free SSL certificates from Let's Encrypt using Certbot.
        </p>
      </div>

      <div className="space-y-4">
        <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
          <h4 className="text-yellow-400 font-medium mb-2">⚠️ Prerequisites</h4>
          <ul className="text-gray-300 text-sm space-y-1">
            <li>✓ DNS configured and propagated (pointing to your server)</li>
            <li>✓ Port 80 and 443 open on your firewall</li>
            <li>✓ Nginx installed and running</li>
          </ul>
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 1: Install Certbot</h4>
          <p className="text-gray-300 mb-3">
            Install Certbot and the Nginx plugin:
          </p>
          <CodeBlock
            code={`sudo apt update
sudo apt install nginx certbot python3-certbot-nginx -y`}
            title="Install Certbot"
            language="bash"
          />
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 2: Configure Nginx</h4>
          <p className="text-gray-300 mb-3">
            Ensure Nginx is configured to proxy requests to SiteHub. See <code className="bg-gray-900 px-2 py-1 rounded text-sm">PRODUCTION_SETUP.md</code> for full Nginx configuration.
          </p>
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 3: Obtain SSL Certificate</h4>
          <p className="text-gray-300 mb-3">
            Run Certbot with your domain name:
          </p>
          <CodeBlock
            code={`sudo certbot --nginx -d yourdomain.com -d www.yourdomain.com`}
            title="Get SSL Certificate"
            language="bash"
          />
          <p className="text-gray-400 text-sm mt-2">
            Replace <code className="text-white bg-gray-900 px-2 py-1 rounded">yourdomain.com</code> with your actual domain.
            Certbot will automatically configure Nginx and set up HTTPS redirects.
          </p>
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 4: Test Auto-Renewal</h4>
          <p className="text-gray-300 mb-3">
            Verify that automatic certificate renewal is working:
          </p>
          <CodeBlock
            code={`sudo certbot renew --dry-run`}
            title="Test Renewal"
            language="bash"
          />
          <p className="text-gray-400 text-sm mt-2">
            Certificates automatically renew every 60 days. This command tests the renewal process without actually renewing.
          </p>
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 5: Verify HTTPS</h4>
          <p className="text-gray-300 mb-3">
            Visit your site to confirm SSL is working:
          </p>
          <CodeBlock
            code={`https://yourdomain.com`}
            title="Verify HTTPS"
          />
          <p className="text-gray-400 text-sm mt-2">
            You should see a padlock icon in your browser's address bar.
          </p>
        </div>

        <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
          <h4 className="text-red-400 font-medium mb-2">🔧 Troubleshooting</h4>
          <div className="text-gray-300 text-sm space-y-2">
            <p><strong>DNS not propagated:</strong> Wait longer (up to 24 hours) or check with <code className="bg-gray-900 px-1 rounded">dig yourdomain.com +short</code></p>
            <p><strong>Port 80 blocked:</strong> Check firewall with <code className="bg-gray-900 px-1 rounded">sudo ufw status</code></p>
            <p><strong>Cloudflare proxy:</strong> Disable orange cloud during initial SSL setup</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SiteContent() {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-semibold text-white mb-3">Add Your First Site</h3>
        <p className="text-gray-400">
          Complete walkthrough for creating and deploying your first site on SiteHub.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 1: Navigate to Sites Page</h4>
          <p className="text-gray-300 mb-3">
            Click <strong>Sites</strong> in the left sidebar navigation menu.
          </p>
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 2: Create New Site</h4>
          <p className="text-gray-300 mb-3">
            Click the <strong className="text-primary-500">+ New Site</strong> button in the top right corner.
          </p>
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 3: Fill in Site Details</h4>
          <p className="text-gray-300 mb-3">
            Complete the site creation form with these required fields:
          </p>
          <div className="bg-gray-900 rounded-lg p-4 space-y-3">
            <div>
              <label className="text-gray-400 text-sm">Name</label>
              <p className="text-white">Your site's display name (e.g., "My Awesome Site")</p>
            </div>
            <div>
              <label className="text-gray-400 text-sm">Slug</label>
              <p className="text-white">URL-friendly identifier (e.g., "my-awesome-site")</p>
            </div>
            <div>
              <label className="text-gray-400 text-sm">Domain</label>
              <p className="text-white">Your custom domain (e.g., "example.com")</p>
            </div>
            <div>
              <label className="text-gray-400 text-sm">GitHub Repository (Optional)</label>
              <p className="text-white">Format: username/repository (e.g., "john/my-site")</p>
            </div>
            <div>
              <label className="text-gray-400 text-sm">Branch (Optional)</label>
              <p className="text-white">Default: main</p>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 4: Upload Site Files</h4>
          <p className="text-gray-300 mb-3">
            You have two options for adding site content:
          </p>
          <div className="space-y-3">
            <div className="bg-gray-900 rounded-lg p-4">
              <h5 className="text-white font-medium mb-2">Option A: Manual Upload</h5>
              <p className="text-gray-300 text-sm mb-2">Place your site files on the server:</p>
              <CodeBlock
                code={`platform/sites/yourdomain.com/index.html`}
                title="File Location"
              />
            </div>
            <div className="bg-gray-900 rounded-lg p-4">
              <h5 className="text-white font-medium mb-2">Option B: GitHub Auto-Deploy</h5>
              <p className="text-gray-300 text-sm mb-2">Connect a GitHub repository for automatic deployments on push.</p>
              <ol className="list-decimal list-inside text-gray-400 text-sm space-y-1 ml-2">
                <li>Add GitHub repo in site settings</li>
                <li>Configure webhook (automatic)</li>
                <li>Push to trigger deployment</li>
              </ol>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 5: Deploy Your Site</h4>
          <p className="text-gray-300 mb-3">
            If using GitHub, deployment happens automatically. For manual uploads:
          </p>
          <ol className="list-decimal list-inside text-gray-300 space-y-2 ml-2">
            <li>Click on your site from the Sites list</li>
            <li>Click the <strong className="text-primary-500">Deploy</strong> button</li>
            <li>Monitor deployment progress in the Deployments tab</li>
          </ol>
        </div>

        <div>
          <h4 className="text-lg font-medium text-white mb-2">Step 6: Verify Your Site</h4>
          <p className="text-gray-300 mb-3">
            Once deployment completes, visit your site:
          </p>
          <CodeBlock
            code={`https://yourdomain.com`}
            title="Visit Your Site"
          />
          <p className="text-gray-400 text-sm mt-2">
            Your site should now be live and accessible! 🎉
          </p>
        </div>

        <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
          <h4 className="text-green-400 font-medium mb-2">✅ Success Checklist</h4>
          <ul className="text-gray-300 text-sm space-y-1">
            <li>✓ Site created in admin dashboard</li>
            <li>✓ DNS pointing to server</li>
            <li>✓ SSL certificate obtained</li>
            <li>✓ Site files uploaded or GitHub connected</li>
            <li>✓ Deployment completed successfully</li>
            <li>✓ Site accessible via HTTPS</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
